import React from "react";
import {
  HERO_CONTENT,
  ABOUT_CONTENT,
} from "../mock/mockData";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "../components/ui/carousel";
import { ArrowRight, ArrowDownRight, Newspaper } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { apiClient } from "../lib/apiClient";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import { ContactsSection } from "../components/ContactsSection";


const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) {
    const headerOffset = 110; // Header height
    const elementPosition = el.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth"
    });
  }
};

export default function HomePage() {
  const { lang, setLang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [news, setNews] = React.useState([]);
  const [loadingNews, setLoadingNews] = React.useState(true);
  const [aboutContent, setAboutContent] = React.useState(null);
  const [loadingAbout, setLoadingAbout] = React.useState(true);
  const [whatWeDoIntro, setWhatWeDoIntro] = React.useState(null);
  const [whatWeDoSlides, setWhatWeDoSlides] = React.useState([]);
  const [loadingWhatWeDo, setLoadingWhatWeDo] = React.useState(true);

  const tHero = HERO_CONTENT[lang];

  // Handle scroll to anchor from navigation
  React.useEffect(() => {
    if (location.state?.scrollTo) {
      const targetId = location.state.scrollTo;
      
      // Function to try scrolling with retries
      const attemptScroll = (attempt = 0) => {
        const element = document.getElementById(targetId);
        
        if (element) {
          // Element found, scroll to it
          scrollToId(targetId);
          // Clear the state to prevent re-scrolling on re-renders
          navigate(location.pathname, { replace: true, state: {} });
        } else if (attempt < 10) {
          // Element not found yet, try again after a delay
          setTimeout(() => attemptScroll(attempt + 1), 200);
        }
      };
      
      // Start attempting to scroll after a short delay
      setTimeout(() => attemptScroll(), 300);
    }
  }, [location.state, navigate, location.pathname]);

  React.useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoadingNews(true);
        const response = await apiClient.get("/public/news", {
          params: { lang, published_only: true, limit: 6 },
        });
        if (Array.isArray(response.data) && response.data.length) {
          setNews(response.data);
        } else {
          setNews([]);
        }
      } catch (err) {
        console.error("Failed to load public news, falling back to mock", err);
        setNews([]);
      } finally {
        setLoadingNews(false);
      }
    };

    fetchNews();
  }, [lang]);

  // Fetch "Who we are" content from API
  React.useEffect(() => {
    const fetchAboutContent = async () => {
      try {
        setLoadingAbout(true);
        const response = await apiClient.get("/public/static-content/who-we-are", {
          params: { lang },
        });
        setAboutContent(response.data);
      } catch (err) {
        console.error("Failed to load Who We Are content, using fallback", err);
        // Fallback to mock data
        setAboutContent(ABOUT_CONTENT[lang]);
      } finally {
        setLoadingAbout(false);
      }
    };

    fetchAboutContent();
  }, [lang]);

  // Fetch "What we do" intro text from API
  React.useEffect(() => {
    const fetchWhatWeDoIntro = async () => {
      try {
        const response = await apiClient.get("/public/what-we-do-intro", {
          params: { lang },
        });
        setWhatWeDoIntro(response.data);
      } catch (err) {
        console.error("Failed to load What We Do intro, using fallback", err);
        setWhatWeDoIntro({
          title: lang === "en" ? "What we do" : "Що ми робимо",
          paragraphs: []
        });
      }
    };

    fetchWhatWeDoIntro();
  }, [lang]);

  // Fetch "What we do" slides from API
  React.useEffect(() => {
    const fetchWhatWeDoSlides = async () => {
      try {
        setLoadingWhatWeDo(true);
        const response = await apiClient.get("/public/what-we-do-slides", {
          params: { lang },
        });
        if (Array.isArray(response.data)) {
          // Sort by display_order
          const sortedSlides = response.data.sort((a, b) => (a.order || 0) - (b.order || 0));
          setWhatWeDoSlides(sortedSlides);
        } else {
          setWhatWeDoSlides([]);
        }
      } catch (err) {
        console.error("Failed to load What We Do slides, using fallback", err);
        setWhatWeDoSlides([]);
      } finally {
        setLoadingWhatWeDo(false);
      }
    };

    fetchWhatWeDoSlides();
  }, [lang]);

  // Update homepage meta tags based on language
  React.useEffect(() => {
    const homeTitle = lang === "en"
      ? "Strategic Lobbying Alliance | Ethical Lobbying & Government Relations"
      : "Strategic Lobbying Alliance | Етичний лобізм та урядові відносини";
    
    const homeDescription = lang === "en"
      ? "The global platform uniting American lobbying standards with European transparency principles and local expertise. Ethical lobbying and government relations across US, EU, and EE jurisdictions."
      : "Глобальна платформа, що об'єднує американські стандарти лобізму з європейськими принципами прозорості та локальною експертизою. Етичний лобізм та урядові відносини в юрисдикціях США, ЄС та Східної Європи.";

    document.title = homeTitle;

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = homeDescription;

    // Open Graph tags for homepage
    const ogTags = [
      { property: 'og:title', content: homeTitle },
      { property: 'og:description', content: homeDescription },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: window.location.origin },
    ];

    ogTags.forEach(({ property, content }) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, [lang]);

  const handleNavClick = (anchor) => {
    scrollToId(anchor);
    setMobileOpen(false);
  };

  return (
    <div className="min-h-screen bg-slate-200 text-slate-900 flex flex-col">
      <SiteHeader
        lang={lang}
        onLangChange={setLang}
        onNavClick={handleNavClick}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        showNavigation={true}
      />
      <main className="flex-1">
        <HeroSection lang={lang} content={tHero} />
        <AboutSection id="who-we-are" content={aboutContent} loading={loadingAbout} navigate={navigate} />
        <WhatWeDoSection 
          id="what-we-do" 
          lang={lang} 
          intro={whatWeDoIntro} 
          slides={whatWeDoSlides}
          loading={loadingWhatWeDo}
        />
        <NewsSection id="news" lang={lang} news={news} loading={loadingNews} navigate={navigate} />
        <ContactsSection id="contacts" lang={lang} />
      </main>
      <SiteFooter lang={lang} />
    </div>
  );
}

function HeroSection({ lang, content }) {
  return (
    <section className="relative overflow-hidden bg-slate-100 h-[623px]">
      {/* Background city image with soft overlay */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <img
          src="/assets/hero-city.jpg"
          alt="Global financial district skyline"
          className="h-full w-full object-cover object-center opacity-[0.5] saturate-125"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-100/96 via-slate-100/88 to-slate-100/92" />
        <div className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-white/96 via-white/80 to-transparent" />
      </div>
      <div className="relative mx-auto flex h-full max-w-6xl flex-col gap-10 px-4 py-14 md:flex-row md:items-center md:justify-between md:px-6">
        <div className="max-w-xl space-y-6">
          <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
            {content.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 pt-2">
            <Button
              size="lg"
              className="group rounded-full bg-amber-500 px-6 text-xs font-semibold uppercase tracking-[0.2em] text-slate-900 shadow-lg shadow-amber-500/40 transition-colors hover:bg-amber-400"
              onClick={() => scrollToId("who-we-are")}
            >
              {content.ctaLabel}
              <ArrowDownRight className="h-4 w-4 transition-transform group-hover:translate-y-0.5 group-hover:translate-x-0.5" />
            </Button>
          </div>
        </div>
        <div className="mt-8 w-full max-w-md md:mt-0">
          <div className="relative rounded-3xl border border-slate-200/90 bg-slate-50/92 p-5 shadow-[0_22px_60px_rgba(15,23,42,0.22)] backdrop-blur-2xl">
            <div className="absolute inset-0 rounded-3xl border border-white/60" />
            <div className="relative flex flex-col items-center gap-4">
              <img
                src={"/assets/!!!SLA_logo.png"}
                alt="Strategic Lobbying Alliance logo"
                className="h-20 w-auto object-contain sm:h-24"
                loading="eager"
              />
              <p className="text-center text-base font-semibold uppercase tracking-[0.2em] text-amber-700">
                Strategic Lobbying Alliance
              </p>
              <p className="text-center text-sm leading-relaxed text-slate-700">
                {lang === "en"
                  ? "The global platform uniting American lobbying standards with European transparency principles and local expertise."
                  : "Глобальна платформа, що об'єднує американські стандарти лобізму з європейськими принципами прозорості та локальною експертизою."}
              </p>
              <div className="mt-2 grid w-full grid-cols-1 min-[360px]:grid-cols-3 gap-3 text-xs text-slate-800">
                <MiniStat label={lang === "en" ? "Jurisdictions" : "Юрисдикції"} value="USA · EU · Ukraine" />
                <MiniStat
                  label={lang === "en" ? "Focus" : "Фокус"}
                  value={lang === "en" ? "Lobbying & GR" : "Лобізм та GR"}
                />
                <MiniStat 
                  label={lang === "en" ? "Standards" : "Стандарти"} 
                  value={lang === "en" ? "Ethics & Professional" : "Етика та Професіоналізм"} 
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-100 px-2 sm:px-3 py-2 text-[11px] shadow-sm">
      <div className="text-[8px] min-[360px]:text-[7px] min-[600px]:text-[9px] md:text-[10px] uppercase tracking-[0.08em] min-[600px]:tracking-[0.15em] md:tracking-[0.22em] text-slate-600 leading-tight">{label}</div>
      <div className="mt-1 text-[10px] min-[360px]:text-[9px] min-[600px]:text-[11px] md:text-[12px] font-semibold leading-tight text-amber-700">{value}</div>
    </div>
  );
}

function AboutSection({ id, content, loading, navigate }) {
  if (loading || !content) {
    return (
      <section id={id} className="bg-[#041e41] py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <p className="text-center text-slate-400">Завантаження...</p>
        </div>
      </section>
    );
  }

  return (
    <section id={id} className="bg-[#041e41] py-16 sm:py-20">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="mb-8 flex items-center justify-between gap-4">
          <div>
            <p className="text-base font-semibold uppercase tracking-[0.2em] text-amber-400">
              {content.title}
            </p>
          </div>
        </div>
        <Card className="grid gap-8 border-slate-300 bg-slate-50 shadow-xl shadow-slate-900/25 md:grid-cols-[minmax(0,1.3fr),minmax(0,1fr)]">
          <CardContent className="flex flex-col justify-center space-y-4 p-6 sm:p-8">
            {content.body && content.body.map((paragraph, idx) => (
              <div 
                key={idx} 
                className="text-sm leading-relaxed text-slate-700 md:text-[15px] prose prose-sm prose-slate max-w-none"
                dangerouslySetInnerHTML={{ __html: paragraph }}
              />
            ))}
            <div className="pt-2">
              <Button
                size="sm"
                onClick={() => navigate("/about")}
                className="group rounded-full bg-transparent px-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-700 ring-1 ring-amber-500/70 transition-colors hover:bg-amber-500 hover:text-white"
              >
                {content.button_label}
                <ArrowRight className="ml-2 h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </div>
          </CardContent>
          <div className="relative overflow-hidden rounded-b-3xl rounded-t-3xl border-t border-slate-300 md:rounded-l-none md:border-l">
            <img src={content.image_url} alt={content.image_alt} className="h-full w-full object-cover" loading="lazy" />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-900/20 via-transparent to-transparent" />
          </div>
        </Card>
      </div>
    </section>
  );
}

function WhatWeDoSection({ id, lang, intro, slides, loading }) {
  if (loading || !intro) {
    return (
      <section id={id} className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <p className="text-center text-slate-400">
            {lang === "en" ? "Loading..." : "Завантаження..."}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id={id} className="bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="mb-8 max-w-3xl space-y-4">
          <p className="text-base font-semibold uppercase tracking-[0.2em] text-amber-700">
            {intro.title}
          </p>
          {intro.paragraphs && intro.paragraphs.map((p, idx) => (
            <p key={idx} className="text-sm leading-relaxed text-slate-700 md:text-[15px]">
              {p}
            </p>
          ))}
        </div>
        {slides && slides.length > 0 && (
          <div className="relative">
            <Carousel className="w-full" opts={{ loop: true }}>
              <CarouselContent>
                {slides.map((slide) => (
                  <CarouselItem key={slide.id}>
                    <Card className="grid min-h-[260px] gap-8 border-slate-200 bg-slate-50 shadow-xl shadow-slate-900/10 md:grid-cols-[minmax(0,1.3fr),minmax(0,1fr)]">
                      <CardContent className="order-2 md:order-1 flex flex-col justify-center space-y-4 p-6 sm:p-8">
                        <div>
                          <h3 className="font-display text-xl font-semibold tracking-tight text-slate-900">
                            {slide.title}
                          </h3>
                          <p className="mt-2 text-sm leading-relaxed text-slate-700 md:text-[15px]">
                            {slide.intro}
                          </p>
                        </div>
                        <ul className="space-y-2 text-sm leading-relaxed text-slate-700">
                          {slide.bullets && slide.bullets.map((item, idx) => (
                            <li key={idx} className="flex gap-2">
                              <span className="mt-[6px] h-1 w-4 rounded-full bg-amber-600" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                        {slide.cta && slide.cta.trim() !== "" && (
                          <div className="pt-2">
                            <Button
                              size="sm"
                              className="group rounded-full bg-transparent px-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-700 ring-1 ring-amber-500/70 transition-colors hover:bg-amber-500 hover:text-white"
                              onClick={() => scrollToId("contacts")}
                            >
                              {slide.cta}
                              <ArrowRight className="ml-2 h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                            </Button>
                          </div>
                        )}
                      </CardContent>
                      <div className="order-1 md:order-2 relative overflow-hidden rounded-b-3xl rounded-t-3xl border-t border-slate-200 md:rounded-l-none md:border-l min-h-[280px] md:min-h-[400px]">
                        <img src={slide.image_url} alt={slide.title} className="h-full w-full object-cover object-center" loading="lazy" />
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-900/20 via-transparent to-transparent" />
                      </div>
                    </Card>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="hidden md:flex bg-slate-50/95 border-slate-300 text-slate-700 hover:bg-slate-100" />
              <CarouselNext className="hidden md:flex bg-slate-50/95 border-slate-300 text-slate-700 hover:bg-slate-100" />
            </Carousel>
          </div>
        )}
      </div>
    </section>
  );
}

function NewsSection({ id, lang, news, loading, navigate }) {
  const visibleNews = Array.isArray(news) ? news.filter((n) => n.is_published !== false) : [];

  return (
    <section id={id} className="bg-[#041e41] py-16 sm:py-20">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="mb-8 flex items-center justify-between gap-4">
          <div>
            <p className="text-base font-semibold uppercase tracking-[0.2em] text-amber-400">
              {lang === "en" ? "Latest news" : "Останні новини"}
            </p>
          </div>
        </div>
        {loading && (
          <p className="text-sm text-slate-400">{lang === "en" ? "Loading news..." : "Завантаження новин..."}</p>
        )}
        {!loading && visibleNews.length === 0 && (
          <p className="text-sm text-slate-400">
            {lang === "en" ? "No published news yet." : "Опублікованих новин поки немає."}
          </p>
        )}
        <div className="grid gap-6 md:grid-cols-2">
          {visibleNews.map((item) => (
            <Card
              key={item.id}
              className="group flex h-full flex-col overflow-hidden border-slate-200 bg-slate-50 shadow-lg shadow-slate-900/30"
            >
              {item.image_url && (
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={item.image_url}
                    alt={item.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-900/25 via-transparent to-transparent" />
                </div>
              )}
              <CardContent className="flex flex-1 flex-col gap-3 p-5">
                <div className="flex items-center justify-between gap-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">
                  <span>
                    {item.published_at
                      ? new Date(item.published_at).toLocaleDateString(lang === "en" ? "en-GB" : "uk-UA")
                      : lang === "en"
                      ? "Invalid date"
                      : "Невірна дата"}
                  </span>
                  <div className="flex flex-wrap gap-1 text-[10px]">
                    {item.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-700"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <h3 className="font-display text-[17px] font-semibold tracking-tight text-slate-900">
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed text-slate-700">
                  {item.summary && item.summary.length > 120
                    ? `${item.summary.slice(0, 120).trimEnd()}…`
                    : item.summary}
                </p>
                <Button
                  type="button"
                  size="sm"
                  className="group mt-1 inline-flex items-center gap-2 rounded-full bg-transparent px-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-700 ring-1 ring-amber-500/70 transition-colors hover:bg-amber-500 hover:text-white"
                  onClick={() => navigate(`/news/${item.slug}`)}
                >
                  <Newspaper className="h-3.5 w-3.5" />
                  <span>{lang === "en" ? "Read more" : "Докладніше"}</span>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}
